import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { Route, Routes, useLocation } from "react-router-dom";
import {
  AttachMoneyRounded,
  BatteryFullRounded,
  ClassRounded,
  CreditCard,
  InsertEmoticonRounded,
  LogoutRounded,
  MonetizationOn,
  MoneyRounded,
  PeopleRounded,
  ProductionQuantityLimitsRounded,
  StoreRounded,
} from "@mui/icons-material";
import {
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
  GRADIENT_MAIN,
} from "../../../data/colors/Colors";
import { Stack } from "@mui/material";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { DrawerItem, DrawerItemView } from "./DrawerItemView";
import { STRINGS_ACTIONS, STRINGS_LOGOUT } from "../../../data/strings/Strings";
import { CustomTextView } from "../../component/CustomTextView";
import { Error404View } from "../../component/404View";
import { CategoriesView } from "../categories/CategoriesView";
import { CategoryDetailsView } from "../categories/categoryDetails/CategoryDetailsView";
import { CustomersView } from "../customers/CustomersView";
import { STORAGE_KEYS } from "../../../data/constant/Constants";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { ProductsView } from "../products/ProductsView";
import { ProductDetailsView } from "../products/productDetails/ProductDetailsView";
import { CustomerDetailsView } from "../customers/details/CustomerDetailsView";
import { TransactionsView } from "../transactions/TransactionsView";
import { OrdersView } from "../orders/OrdersView";
import { StoresView } from "../stores/StoresView";
import { StoreDetailsView } from "../stores/details/StoreDetailsView";
import { OrderDetailsView } from "../orders/details/OrderDetailsView";

const mobileDrawerWidth = 280;
const drawerWidth = 340;

export function HomeView() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const location = useLocation();

  const drawerRouterItems: Array<DrawerItem> = [
    {
      isRoute: true,
      title: "Customers",
      icon: <PeopleRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_CUSTOMERS,
      href: ROUTE_ENDPOINTS.HOME_CUSTOMERS,
      onCloseDrawer: () => setMobileOpen(false),
    },
  ];

  const productsDrawerRouterItems: Array<DrawerItem> = [
    {
      isRoute: true,
      title: "Categories",
      icon: <ClassRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_CATEGORIES,
      href: ROUTE_ENDPOINTS.HOME_CATEGORIES,
      onCloseDrawer: () => setMobileOpen(false),
    },
    {
      isRoute: true,
      title: "Stores",
      icon: <StoreRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_STORES,
      href: ROUTE_ENDPOINTS.HOME_STORES,
      onCloseDrawer: () => setMobileOpen(false),
    },
    {
      isRoute: true,
      title: "Products",
      icon: <ProductionQuantityLimitsRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_PRODUCTS,
      href: ROUTE_ENDPOINTS.HOME_PRODUCTS,
      onCloseDrawer: () => setMobileOpen(false),
    },
    {
      isRoute: true,
      title: "Orders",
      icon: <CreditCard />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_ORDERS,
      href: ROUTE_ENDPOINTS.HOME_ORDERS,
      onCloseDrawer: () => setMobileOpen(false),
    },
    {
      isRoute: true,
      title: "Transactions",
      icon: <AttachMoneyRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_TRANSACTIONS,
      href: ROUTE_ENDPOINTS.HOME_TRANSACTIONS,
      onCloseDrawer: () => setMobileOpen(false),
    },
  ];

  const drawerButtonsItems: Array<DrawerItem> = [
    {
      isRoute: false,
      title: STRINGS_LOGOUT,
      icon: <LogoutRounded />,
      onClick: () => {
        SharedPreferences.clear();
      },
    },
  ];

  const drawer = (
    <Stack
      sx={{
        backdropFilter: mobileOpen ? "blur(10px)" : undefined,
        background: COLOR_WHITE,
        borderRadius: mobileOpen ? 0 : 6,
        border: "1px solid gray",
        height: "100%",
        overflow: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Stack
        spacing={0}
        pt={2}
        height={"100%"}
        justifyContent={"space-between"}
      >
        <Stack>
          {drawerRouterItems.map((item) => (
            <DrawerItemView
              icon={item.icon}
              isRoute={item.isRoute}
              key={item.title}
              title={item.title}
              isSelected={item.isSelected}
              href={item.href}
              onCloseDrawer={item.onCloseDrawer}
            />
          ))}
          {productsDrawerRouterItems.map((item) => (
            <DrawerItemView
              icon={item.icon}
              isRoute={item.isRoute}
              key={item.title}
              title={item.title}
              isSelected={item.isSelected}
              href={item.href}
              onCloseDrawer={item.onCloseDrawer}
            />
          ))}
        </Stack>
        <Stack pt={2} pb={2}>
          {drawerButtonsItems.map((item) => (
            <DrawerItemView
              key={item.title}
              icon={item.icon}
              isRoute={item.isRoute}
              onClick={item.onClick}
              title={item.title}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
  return (
    <Stack
      width={"100%"}
      height={"100vh"}
      direction={"row"}
      overflow={"auto"}
      sx={{ background: COLOR_WHITE }}
    >
      <Box
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        overflow={"hidden"}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: mobileDrawerWidth,
              background: "transparent",
            },
          }}
        >
          <Toolbar />
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              background: "transparent",
              width: drawerWidth,
              borderWidth: 0,
              p: 2,
            },
          }}
          open
        >
          <Toolbar />
          {drawer}
        </Drawer>
      </Box>
      <Stack
        position="fixed"
        width={"100%"}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: COLOR_MAIN,
          borderBottomLeftRadius: 20,
          borderBottomRightRadius: 20,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: 0, mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon sx={{ color: "white" }} />
          </IconButton>
          <CustomTextView
            text={"SoomTech Admin Panel"}
            variant="h5"
            fontWeight="bold"
            textColor={COLOR_WHITE}
          />
        </Toolbar>
      </Stack>
      <Stack
        pr={2}
        pt={2}
        pl={{ xs: 2, sm: 0, md: 0, lg: 0, xl: 0 }}
        zIndex={2}
        width={"100%"}
      >
        <Toolbar />
        <Stack width={"100%"} paddingBottom={2}>
          <Stack
            sx={{
              borderRadius: 6,
              backgroundColor: COLOR_WHITE,
              border: "1px solid gray",
            }}
          >
            <Routes>
              <Route
                path={ROUTE_ENDPOINTS.CUSTOMERS}
                element={<CustomersView />}
              />
              <Route
                path={`${ROUTE_ENDPOINTS.CUSTOMERS}/:id`}
                element={<CustomerDetailsView />}
              />
              <Route
                path={ROUTE_ENDPOINTS.CATEGORIES}
                element={<CategoriesView />}
              />
              <Route
                path={`${ROUTE_ENDPOINTS.CATEGORIES}/:id`}
                element={<CategoryDetailsView />}
              />
              <Route path={ROUTE_ENDPOINTS.STORES} element={<StoresView />} />
              <Route
                path={`${ROUTE_ENDPOINTS.STORES}/:id`}
                element={<StoreDetailsView />}
              />
              <Route
                path={ROUTE_ENDPOINTS.PRODUCTS}
                element={<ProductsView />}
              />
              <Route
                path={`${ROUTE_ENDPOINTS.PRODUCTS}/:id`}
                element={<ProductDetailsView />}
              />
              <Route path={ROUTE_ENDPOINTS.ORDERS} element={<OrdersView />} />
              <Route
                path={`${ROUTE_ENDPOINTS.ORDERS}/:id`}
                element={<OrderDetailsView />}
              />
              <Route
                path={ROUTE_ENDPOINTS.TRANSACTIONS}
                element={<TransactionsView />}
              />

              <Route path="*" element={<Error404View />} />
            </Routes>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
